import { useCallback, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  ACCEPT,
  MV_CSV_COLUMNS,
  calendarService,
  subscriptionService,
  taskService,
} from "@miplatform/api";
import {
  DATE_TIME_FORMATS,
  HTTP_STATUSES,
  LABEL_CONFIG_AREAS,
  LABEL_CONFIG_DEPENDENT_TYPES,
  MAX_PAGE_SIZE,
  NONE,
  PLATFORM_MODULES,
  PRINT_OPTIONS,
  PRINT_ORIENTATION,
  TIME_UNITS,
} from "@miplatform/constants";
import { useFeatureFlags } from "@miplatform/feature-flags";
import { useComponentState, useDebounce, useRouter } from "@miplatform/hooks";
import {
  buildActivityDateFilter,
  buildSearchQuery,
  endOf,
  formatDate,
  getDate,
  isSameDay,
  startOf,
} from "@miplatform/utils";
import { usePrint, useSession } from "../../hooks";
import {
  getOrdersRequestSort,
  useOrdersSearch,
} from "./OrdersSearch/hooks/useOrdersSearch";
import {
  ALL_OPTION,
  CAPACITY_TRACKERS_STATUSES,
  ORDERS_COLUMNS_PRINT,
  ORDERS_ITEMS_PRINT_HEADER_FIELDS,
  ORDERS_ITEMS_PRINT_HIGHLIGHTED_HEADER_FIELD,
  ORDERS_ITEMS_PRINT_SECTIONS,
  ORDERS_TABS,
  ORDERS_TABS_NAMES,
  TABS_WITH_COUNT_CHIPS,
} from "./ordersConstants";

export const useOrdersViewModel = () => {
  const {
    session: { tenantId },
  } = useSession();
  const { params, navigate } = useRouter();
  const { tab } = params;
  const { featureFlags } = useFeatureFlags();

  const dateRangeRef = useRef({
    startDate: startOf(TIME_UNITS.ISO_WEEK, getDate()),
    endDate: endOf(TIME_UNITS.ISO_WEEK, getDate()),
  });

  const getTabFromUrl = (urlTab) => {
    const tabMap = {
      all: ORDERS_TABS_NAMES.ALL,
      new: ORDERS_TABS_NAMES.NEW,
      reschedule: ORDERS_TABS_NAMES.RESCHEDULE,
      scheduled: ORDERS_TABS_NAMES.SCHEDULED,
      confirmed: ORDERS_TABS_NAMES.CONFIRMED,
      assigned: ORDERS_TABS_NAMES.ASSIGNED,
      completed: ORDERS_TABS_NAMES.COMPLETE,
      holds: ORDERS_TABS_NAMES.HOLDS,
      assignments: ORDERS_TABS_NAMES.ASSIGNMENTS,
      "follow-up": ORDERS_TABS_NAMES.FOLLOW_UP,
      cancelled: ORDERS_TABS_NAMES.CANCELLED,
    };
    return tabMap[urlTab] || ORDERS_TABS_NAMES.ALL;
  };

  const getInitialTab = () => {
    if (tab) {
      const tabValue = getTabFromUrl(tab);
      return (
        ORDERS_TABS.find(({ value }) => value === tabValue) || ORDERS_TABS[0]
      );
    }
    return ORDERS_TABS[0];
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [tabs, setTabs] = useState(ORDERS_TABS);
  const [orderItems, setOrderItems] = useState([]);
  const [search, setSearch] = useState("");
  const [orderReasons, setOrderReasons] = useState({});
  const [filters, setFilters] = useState({
    selectedTrade: ALL_OPTION.value,
    selectedOrderTemplate: null,
    dateRange: { ...dateRangeRef.current },
  });
  const {
    filters: sidebarFilters,
    setFilters: setSidebarFilters,
    initialFilters: initialSidebarFilters,
    getOrdersSearchFilterQuery,
    resetFilters,
  } = useOrdersSearch({
    search,
  });
  const [orderTypeCount, setOrderTypeCount] = useState({});
  const [refresh, setRefresh] = useState(0);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [orderCardConfig, setOrderCardConfig] = useState(null);
  const [orderHoverCardConfig, setOrderHoverCardConfig] = useState(null);
  const [account, setAccount] = useState(null);
  const [tenantHolidays, setTenantHolidays] = useState([]);
  const [dateInScheduledTab, setDateInScheduledTab] = useState(moment());
  const { handlePrintTable, handlePrintSectionsPages } = usePrint(account);
  const debouncedSearch = useDebounce(search, 500);
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const isCapacityTrackerVisible = activeTab.status.some((status) =>
    CAPACITY_TRACKERS_STATUSES.has(status)
  );
  const filteredTabs = useMemo(
    () =>
      tabs.filter((tab) => {
        if (tab.featureFlag) {
          return featureFlags[tab.featureFlag];
        }
        return true;
      }),
    [tabs, featureFlags]
  );

  const getUrlFromTab = (tabValue) => {
    const urlMap = {
      [ORDERS_TABS_NAMES.ALL]: "all",
      [ORDERS_TABS_NAMES.NEW]: "new",
      [ORDERS_TABS_NAMES.RESCHEDULE]: "reschedule",
      [ORDERS_TABS_NAMES.SCHEDULED]: "scheduled",
      [ORDERS_TABS_NAMES.CONFIRMED]: "confirmed",
      [ORDERS_TABS_NAMES.ASSIGNED]: "assigned",
      [ORDERS_TABS_NAMES.ASSIGNMENTS]: "assignments",
      [ORDERS_TABS_NAMES.COMPLETE]: "completed",
      [ORDERS_TABS_NAMES.HOLDS]: "holds",
      [ORDERS_TABS_NAMES.FOLLOW_UP]: "follow-up",
      [ORDERS_TABS_NAMES.CANCELLED]: "canceled",
    };
    return urlMap[tabValue] || "all";
  };

  const handleTabChange = (tabValue) => {
    const newTab = ORDERS_TABS.find(({ value }) => value === tabValue);
    if (newTab) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        selectedOrderTemplate:
          newTab.value === ORDERS_TABS_NAMES.ALL
            ? null
            : prevFilters.selectedOrderTemplate,
        dateRange: { ...dateRangeRef.current },
      }));
      setActiveTab(newTab);
      resetFilters();
      const urlTab = getUrlFromTab(tabValue);
      navigate(`/scheduling/orders/${urlTab}`);
    }
  };

  const handlePrintModalOpen = () => setIsPrintModalOpen(true);

  const handlePrintModalClose = () => setIsPrintModalOpen(false);

  const handleSpreadsheetDownload = () => {
    return stateManager.run(async () => {
      const response = await taskService.order.getAll({
        top: MAX_PAGE_SIZE,
        headers: {
          [ACCEPT]: "text/csv; charset=utf-8",
          [MV_CSV_COLUMNS]:
            "account.name:Account, home.communityName:Community, home.address.addressLine1:AddressLine1, home.address.addressLine2:AddressLine2, home.address.city:City, home.address.regionCode:State, home.address.postalCode:Zip, createdOn:Entered|MM-dd-yyyy, orderStatus:Status, orderTemplateName:Order, priority:Priority, requestedStartDate:Requested Start Date|MM-dd-yyyy, approvedStartDate:Approved Start Date|MM-dd-yyyy, confirmedScheduleDate:Confirmed Date|MM-dd-yyyy, completeDate:Complete Date|MM-dd-yyyy, cancelledDate:Cancelled Date|MM-dd-yyyy, requester.label:Requester Title, requester.searchField:Requester Name, requester.phone:Requester Phone, requester.email:Requester Email, notes:Notes, holdReason:Hold Reason, holdStartDate:Hold Start Date|MM-dd-yyyy, followUpDate:Follow Up Date|MM-dd-yyyy",
        },
        ...getOrdersRequestParams(),
      });

      if (response.status === HTTP_STATUSES.OK) {
        const csv = response.data;

        const blob = new Blob([csv], {
          type: "text/csv; charset=utf-8;",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        const timestamp = formatDate(
          getDate(),
          DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD_HH_mm_A
        );
        // e.g. mibuild_export_2026-01-14 08-50 AM.csv
        link.download = `mibuild_export_${timestamp}.csv`;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return true;
      }

      return false;
    });
  };

  const handleResetDateRange = useCallback(
    () =>
      setFilters((prevFilters) => ({
        ...prevFilters,
        dateRange: {
          startDate: startOf(TIME_UNITS.DATE, getDate()),
          endDate: endOf(TIME_UNITS.DATE, getDate()),
        },
      })),
    [setFilters]
  );

  const isDateRangeDisabled = () =>
    isSameDay(filters.dateRange.startDate, getDate()) &&
    isSameDay(filters.dateRange.endDate, getDate());

  const handleFilterChange = useCallback(
    ({ orderTemplateId, orderTemplateLabel, dateRange }) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...(orderTemplateId !== undefined && {
          selectedOrderTemplate:
            orderTemplateId === null ||
            prevFilters.selectedOrderTemplate?.id === orderTemplateId
              ? null
              : { id: orderTemplateId, label: orderTemplateLabel },
        }),
        ...(dateRange && {
          dateRange: {
            startDate: dateRange.startDate || prevFilters.dateRange.startDate,
            endDate: dateRange.endDate || prevFilters.dateRange.endDate,
          },
        }),
      }));
    },
    [setFilters]
  );

  const handleRefresh = useCallback(
    () => setRefresh((prev) => prev + 1),
    [setRefresh]
  );

  const getCommonFilters = () => {
    const searchQuery = buildSearchQuery(search, [
      "home/address/addressLine1",
      "home/communityName",
      "home/builderName",
      "home/address/city",
      "orderNumber",
    ]);
    return {
      orderTemplateModule: { eq: PLATFORM_MODULES.MI_SCHEDULING },
      ...(activeTab.status && {
        orderStatus: {
          in: activeTab.status,
        },
      }),
      ...(activeTab.customFilter && {
        ...activeTab.customFilter,
      }),
      ...(filters.selectedTrade !== ALL_OPTION.value && {
        tradeId: { type: "guid", value: filters.selectedTrade },
      }),
      ...(activeTab.dateRange && {
        [activeTab.dateRange]: {
          ge: startOf(TIME_UNITS.DAY, filters.dateRange.startDate).toDate(),
          le: endOf(TIME_UNITS.DAY, filters.dateRange.endDate).toDate(),
        },
      }),
      ...searchQuery,
    };
  };

  const getOrdersRequestParams = () => {
    const hasSidebarFilters =
      JSON.stringify(sidebarFilters) !== JSON.stringify(initialSidebarFilters);

    const sidebarFiltersQuery = getOrdersSearchFilterQuery(sidebarFilters);
    const hasGroupOrSort =
      sidebarFilters.groupBy !== NONE || sidebarFilters.sortBy !== NONE;

    return {
      sort: getOrdersRequestSort({
        activeTabSort: activeTab.sort,
        sidebarSort: sidebarFiltersQuery.sort,
        prioritizeSidebar: hasGroupOrSort,
      }),
      filters: {
        ...(filters.selectedOrderTemplate && {
          orderTemplateId: {
            eq: { type: "guid", value: filters.selectedOrderTemplate.id },
          },
        }),
        ...getCommonFilters(),
        ...(hasSidebarFilters && sidebarFiltersQuery.filters),
      },
    };
  };

  const getAccount = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.subscriptions.get(tenantId);

      if (response.status === HTTP_STATUSES.OK) {
        setAccount(response.data);
      }
    });
  };

  const getOrderItems = ({ top = 25, skip } = {}) => {
    return stateManager.run(async () =>
      taskService.order.getAll({
        top,
        skip,
        ...getOrdersRequestParams(),
      })
    );
  };

  const getOrderCardConfig = () => {
    stateManager.run(async () => {
      const response = await taskService.labelConfig.get({
        dependentType: LABEL_CONFIG_DEPENDENT_TYPES.ORDERS,
        area: LABEL_CONFIG_AREAS.CARD,
        isActive: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderCardConfig(response.data);
      }
    });
    stateManager.run(async () => {
      const response = await taskService.labelConfig.get({
        dependentType: LABEL_CONFIG_DEPENDENT_TYPES.ORDERS,
        area: LABEL_CONFIG_AREAS.HOVER_CARD,
        isActive: true,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderHoverCardConfig(response.data);
      }
    });
  };

  const getOrderItemsCount = () => {
    stateManager.run(async () => {
      const response = await taskService.order.getCounts({
        filters: {
          orderTemplateModule: { eq: PLATFORM_MODULES.MI_SCHEDULING },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        const countMap = Object.fromEntries(
          response.data.map((item) => [item.name, item.count || 0])
        );

        setTabs(
          tabs.map((tab) => {
            const shouldShowChip = TABS_WITH_COUNT_CHIPS.includes(tab.value);
            if (shouldShowChip) {
              const statusToCount = tab.countStatus || tab.status;
              const totalCount = statusToCount.reduce(
                (sum, status) => sum + (countMap[status] || 0),
                0
              );

              return {
                ...tab,

                ...(totalCount > 0
                  ? { chipLabel: totalCount }
                  : { chipLabel: null }),
              };
            }
            return tab;
          })
        );
      }
    });
  };

  const getOrderTypesCount = () => {
    stateManager.run(async () => {
      const response = await taskService.order.getOrderTemplateSummary({
        filters: getCommonFilters(),
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderTypeCount(
          response.data.reduce((acc, orderType) => {
            acc[orderType.id] = orderType.count;
            return acc;
          }, {})
        );
      }
    });
  };

  const handlePrintOrders = async (printOption) => {
    return stateManager.run(async () => {
      const response = await taskService.order.getDetailedAll(
        getOrdersRequestParams()
      );

      if (response.status === HTTP_STATUSES.OK) {
        if (printOption === PRINT_OPTIONS.TABLE) {
          handlePrintTable(response.data, ORDERS_COLUMNS_PRINT, {
            orientation: PRINT_ORIENTATION.LANDSCAPE,
          });
        } else {
          handlePrintSectionsPages(response.data, ORDERS_ITEMS_PRINT_SECTIONS, {
            headerFields: ORDERS_ITEMS_PRINT_HEADER_FIELDS,
            highlightedHeaderField: ORDERS_ITEMS_PRINT_HIGHLIGHTED_HEADER_FIELD,
            orientation: PRINT_ORIENTATION.PORTRAIT,
          });
        }

        return true;
      }

      return false;
    });
  };

  const getTenantHolidays = async () => {
    const response = await calendarService.tenantHolidays.getAll();
    if (response.status === HTTP_STATUSES.OK) {
      setTenantHolidays(response.data || []);
    }
  };

  const getHoldReasons = () => {
    stateManager.run(async () => {
      const response = await taskService.orderReasons.getHoldReasons();
      if (response.status === HTTP_STATUSES.OK) {
        const formattedData = response.data?.labels.map((reason) => ({
          value: reason,
          label: reason,
        }));
        setOrderReasons((prev) => ({
          ...prev,
          holdReasons: formattedData,
        }));
      }
    });
  };

  const getRescheduleReasons = () => {
    stateManager.run(async () => {
      const response = await taskService.orderReasons.getRescheduleReasons();
      if (response.status === HTTP_STATUSES.OK) {
        const formattedData =
          response.data?.map((reason) => ({
            value: reason.id,
            label: reason.label,
            color: reason.color,
          })) || [];
        setOrderReasons((prev) => ({
          ...prev,
          rescheduleReasons: formattedData,
        }));
      }
    });
  };

  const getOrderActivity = async (orderId, dateRange) => {
    return stateManager.run(async () => {
      const response = await taskService.activities.getAll({
        params: {
          refId: `task-service/orders/${orderId}`,
        },
        filters: buildActivityDateFilter(dateRange),
      });
      return response;
    });
  };

  return {
    account,
    activeTab,
    orderCardConfig,
    orderHoverCardConfig,
    orderTypeCount,
    filters,
    tabs: filteredTabs,
    refresh,
    orderItems,
    isPrintModalOpen,
    isLoading,
    search,
    debouncedSearch,
    orderReasons,
    isCapacityTrackerVisible,
    isDateRangeDisabled,
    handleTabChange,
    setTabs,
    setSearch,
    setFilters,
    setOrderItems,
    getAccount,
    getOrderItems,
    getOrderItemsCount,
    getOrderTypesCount,
    getOrderCardConfig,
    handlePrintModalOpen,
    handlePrintModalClose,
    handleSpreadsheetDownload,
    handleFilterChange,
    handleResetDateRange,
    handleRefresh,
    handlePrintOrders,
    tenantHolidays,
    getTenantHolidays,
    getHoldReasons,
    getRescheduleReasons,
    dateInScheduledTab,
    setDateInScheduledTab,
    getOrderActivity,
    sidebarFilters,
    setSidebarFilters,
    initialSidebarFilters,
  };
};
